import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { ProductPricing } from '@services/merch/types/api/create-product';
import { MerchOrderVariant } from '@services/merch/types/api/create-merch-order';

import scrollOnError from '@webapp/utils/release/scroll-on-error';
import {
  merchReleaseStepCompletedEvent,
  merchReleaseStepViewedEvent,
} from '@webapp/utils/rudderstack/events';

import AppState from '@webapp/store/state';
import { Merch, ProductSample } from '@webapp/store/merch/state';
import {
  assetOwners,
  getOwnershipTotal,
} from '@webapp/store/ownership/selectors';
import {
  CreateOwnership,
  GetOwnerships,
  ReassignSplits,
  RemoveOwnership,
} from '@webapp/store/ownership/action-creators';
import { getProductSampleVariants } from '@webapp/store/merch/selectors';
import {
  CreateSampleOrder,
  UpdateMerchStore,
} from '@webapp/store/merch/action-creators';
import {
  VALIDATE_MERCH_PRODUCT_SAMPLE_ORDER,
  CREATE_MERCH_PRODUCT_SAMPLE_ORDER,
  GET_MERCH_SAMPLE_ORDER_SHIPPING_COST,
  GET_MERCH_SAMPLE_ORDER_SHIPPING_COST_ERROR,
  VALIDATE_MERCH_PRODUCT_SAMPLE_ORDER_ERROR,
} from '@webapp/store/merch/actions';

import MerchProductSample from './product-sample';

import { getOrderValidationErrorId } from '@webapp/pages/merch/utils';

import OwnershipCtrl from '@webapp/controllers/track/ownership';
import { MerchProductQuantity } from '@webapp/controllers/merch/merch-product-quantity';
import MerchProductDetail, {
  MerchProductDetailData,
} from '@webapp/controllers/merch/merch-product-detail';
import ButtonLoadingCtrl from '@webapp/controllers/button-loading';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import { getWidth } from '@components/styles';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import RightArrow from '@components/basics/icons/right-arrow';
import { Ownership } from '@services/ownership/types/ownership';
import { NewOwnership } from '@services/ownership/types/new-ownership';
import arrayMove from 'array-move';
import { assetOwnersByType } from '@webapp/store/ownership/selectors';
import { ValidOwnershipType } from '@services/ownership/types/valid-ownership-type';
import { SplitUpdate } from '@services/ownership/types/split-update';
import isEqual from 'lodash/isEqual';

interface MerchPricingOwnersProps {
  className?: string;
  isEdit?: boolean;
  onNext: () => void;
}

type MerchQuantityData = {
  quantity?: number;
  isUnlimited?: boolean;
};
interface MerchPricingOwnerStateProps {
  active?: Partial<Merch>;
  productId: string;
  printifyProductId?: number;
  productData: MerchProductDetailData;
  ownerShipAsset: {
    [ownershipId: string]: Ownership;
  };
  pricingData: ProductPricing;
  invalidOwnershipTotal: boolean;
  isValidSampleOrder: boolean;
  quantityData: MerchQuantityData;
  receiveSample: boolean;
  productSample: ProductSample | null | undefined;
  variants: MerchOrderVariant[];
  ownerShip: Ownership[];
  email?: string;
  artistId?: string;
  artistName?: string;
  isLoading: boolean;
  isCreateOrderLoading: boolean;
  validationErrorId?: string | null;
  isMobile?: boolean;
  isLaptop?: boolean;
}

interface MerchPricingOwnersDispatchProps {
  getOwnerships: (assetId: string) => GetOwnerships;
  createSampleOrderRequest: () => void;
  createOwnership: (newOwner: NewOwnership) => CreateOwnership;
  removeOwnership: (assetId: string, ownershipId: string) => RemoveOwnership;
  getOwnership: (assetId: string) => GetOwnerships;
  updateProductData: (
    productData: Partial<MerchProductDetailData> & {
      quantity?: number;
      isUnlimited?: boolean;
    }
  ) => UpdateMerchStore;
  reassignSplit: (
    ownershipId: ValidOwnershipType,
    split: SplitUpdate[],
    trackId: string,
    newOwnerShip: Ownership[]
  ) => ReassignSplits;
}

const mapStateToProps = (state: AppState) => {
  const productData =
    state.merch.active?.productData ||
    ({
      productName: state.merch.active?.editProduct?.title || '',
      productPrice: state.merch.active?.editProduct?.price.toString() || '',
    } as MerchProductDetailData);

  const productId =
    (state.merch.active?.editProduct
      ? state.merch.active?.editProduct.id
      : state.merch.active?.newProduct
      ? state.merch.active?.newProduct?.productId
      : state.merch.active?.product
      ? state.merch.active?.product?.id.toString()
      : '') || '';
  const receiveSample =
    state.merch.active?.productSample?.receiveSample || false;
  const productSample = state.merch.active?.productSample;
  const editProduct = state.merch.active?.editProduct;
  const newProduct = state.merch.active?.newProduct;
  const variants =
    (productSample &&
      getProductSampleVariants({
        productSample,
        editProduct,
        newProduct,
      })) ??
    [];
  const validationErrorCode =
    state.ui.errors[VALIDATE_MERCH_PRODUCT_SAMPLE_ORDER_ERROR] ||
    state.ui.errors[GET_MERCH_SAMPLE_ORDER_SHIPPING_COST_ERROR];

  const isValidSampleOrder = !state.merch.active?.productSample?.receiveSample
    ? true
    : !!(
        productId &&
        variants.length > 0 &&
        productSample &&
        productSample.shippingDetails?.name &&
        productSample.shippingDetails?.address &&
        productSample.shippingDetails?.city &&
        productSample.shippingDetails?.region &&
        productSample.shippingDetails?.postCode &&
        productSample.shippingDetails?.country &&
        !validationErrorCode
      );

  const isLoading =
    !!state.ui.loading[VALIDATE_MERCH_PRODUCT_SAMPLE_ORDER] ||
    !!state.ui.loading[GET_MERCH_SAMPLE_ORDER_SHIPPING_COST];
  const isCreateOrderLoading =
    !!state.ui.loading[CREATE_MERCH_PRODUCT_SAMPLE_ORDER];

  return {
    productId,
    ownerShipAsset: state.ownership.assets[productId],
    printifyProductId: state.merch.active?.editProduct
      ? state.merch.active?.editProduct.printify[0].itemId
      : state.merch.active?.product?.id,
    invalidOwnershipTotal:
      getOwnershipTotal(state.ownership, productId, 'OWNER') !== 100,
    isValidSampleOrder,
    pricingData: state.merch.active?.newProduct?.pricing || {
      production_cost: state.merch.active?.editProduct?.cost || 0,
      profit_suggested_percentage: 100,
      suggested_price: state.merch.active?.editProduct?.price || 0,
    },
    variants,
    receiveSample,
    productData,
    productSample,
    email: state.artist.active?.email,
    artistId: state.artist.active?.id,
    artistName: state.artist.active?.name,
    quantityData: {
      quantity: state.merch.active?.quantity,
      isUnlimited: state.merch.active?.isUnlimited,
    } as MerchQuantityData,
    isLoading,
    isCreateOrderLoading,
    validationErrorId: getOrderValidationErrorId(validationErrorCode),
    isMobile: state.ui.mobile,
    isLaptop: state.ui.laptop,
    ownerShip: assetOwners(state.ownership, productId, 'OWNER'),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getOwnerships: (assetId: string) => dispatch(new GetOwnerships(assetId)),
  createSampleOrderRequest: () => dispatch(new CreateSampleOrder()),
  updateProductData: (
    productData: Partial<MerchProductDetailData> & {
      quantity?: number;
      isUnlimited?: boolean;
    }
  ) => {
    const obj = {
      productData: {
        productName: productData.productName || '',
        productPrice: productData.productPrice || '',
      },
      quantity: productData.quantity,
      isUnlimited: productData.isUnlimited,
    };

    return dispatch(new UpdateMerchStore({ ...obj }));
  },
  createOwnership: (newOwner: NewOwnership) =>
    dispatch(new CreateOwnership(newOwner)),
  removeOwnership: (assetId: string, ownershipId: string) =>
    dispatch(new RemoveOwnership(assetId, ownershipId)),
  reassignSplit: (
    ownershipId: ValidOwnershipType,
    split: SplitUpdate[],
    trackId: string,
    newOwnerShip: Ownership[]
  ) => dispatch(new ReassignSplits(trackId, ownershipId, split, newOwnerShip)),
});

export const MerchPricingOwners: React.FC<
  MerchPricingOwnersProps &
    MerchPricingOwnerStateProps &
    MerchPricingOwnersDispatchProps
> = ({
  invalidOwnershipTotal,
  isValidSampleOrder,
  className,
  ownerShipAsset,
  productId,
  printifyProductId,
  productData,
  pricingData,
  quantityData,
  getOwnerships,
  createOwnership,
  removeOwnership,
  updateProductData,
  onNext,
  receiveSample,
  createSampleOrderRequest,
  productSample,
  variants,
  email,
  artistId,
  artistName,
  isLoading,
  isCreateOrderLoading,
  validationErrorId,
  isEdit,
  isMobile,
  isLaptop,
  reassignSplit,
}) => {
  const { productName, productPrice } = productData;
  const [validateErrors, setValidateErrors] = React.useState(0);
  const [validateForm, setValidateForm] = React.useState(false);
  const [quantity, setQuantity] = React.useState<string>(
    `${quantityData.quantity ?? 10}`
  );
  const [isUnlimited, setIsUnlimited] = React.useState(
    quantityData.isUnlimited ?? false
  );
  const [ownershipSplitArray, setOwnershipSplitArray] = React.useState<
    Ownership[]
  >([]);
  React.useEffect(() => {
    getOwnerships(productId);
    SetReassignSplitErrorForOWNER(false);
  }, [productId, getOwnerships]);

  React.useEffect(() => {
    const newOwnershipSplit = assetOwnersByType(ownerShipAsset, 'OWNER');

    if (
      newOwnershipSplit.length > 0 &&
      !isEqual(newOwnershipSplit, ownershipSplitArray)
    ) {
      setOwnershipSplitArray(newOwnershipSplit);
      if (
        newOwnershipSplit.length > 0 &&
        newOwnershipSplit.reduce((memo, owner) => memo + owner.split, 0) === 100
      ) {
        SetReassignSplitErrorForOWNER(false);
      } else {
        SetReassignSplitErrorForOWNER(true);
      }
    } else if (newOwnershipSplit.length === 0) {
      SetReassignSplitErrorForOWNER(true);
      if (ownershipSplitArray.length) {
        setOwnershipSplitArray(newOwnershipSplit);
      }
    }
  }, [ownerShipAsset, ownershipSplitArray]);

  const [reassignSplitErrorForOWNER, SetReassignSplitErrorForOWNER] =
    React.useState(false);
  const onRemoveOwnerOwnership = (
    ownershipId: string,
    withAutosave: boolean
  ) => {
    const propsOwnershipSplit = assetOwnersByType(ownerShipAsset, 'OWNER');

    let newOwnership =
      ownershipSplitArray.length > 0 &&
      ownershipSplitArray.filter((owner) => owner.assetId === productId)
        .length > 0
        ? ownershipSplitArray
        : propsOwnershipSplit;

    if (withAutosave) {
      return removeOwnership(productId, ownershipId);
    }

    newOwnership = newOwnership.filter(
      (ownerShip) => ownerShip.id !== ownershipId
    );
    setOwnershipSplitArray(newOwnership);
  };
  const updateOwnerSplit = (
    ownershipId: string,
    updatedSplit: number,
    withAutosave: boolean
  ) => {
    if (withAutosave) {
      const ownerShip = ownershipSplitArray.find(
        (ownerShip) => ownerShip.id === ownershipId
      );
      if (!ownerShip) {
        return;
      }
      let newUpdatedArrSplit = ownershipSplitArray.map((ownership) => {
        if (ownership.id === ownershipId) {
          return {
            assetId: productId,
            ownershipId: ownership.id,
            split: updatedSplit,
          };
        }
        return {
          assetId: productId,
          ownershipId: ownership.id,
          split: ownership.split,
        };
      });
      let newOwnerShipSplitArr = ownershipSplitArray.filter(
        (splitArr) => !splitArr.id
      );
      reassignSplit(
        'OWNER',
        newUpdatedArrSplit,
        productId,
        newOwnerShipSplitArr
      );
    } else {
      const newOwnership = ownershipSplitArray.map((ownerShip) => {
        if (ownerShip.id === ownershipId) {
          return {
            ...ownerShip,
            split: updatedSplit,
          };
        }
        return ownerShip;
      });

      if (
        newOwnership.length > 0 &&
        newOwnership.reduce((memo, owner) => memo + owner.split, 0) === 100
      ) {
        SetReassignSplitErrorForOWNER(false);
      } else {
        SetReassignSplitErrorForOWNER(true);
      }

      setOwnershipSplitArray(newOwnership);
    }
  };
  const createOwnershipOnSubmit = (newOwnerShip: Ownership) => {
    const propsOwnershipSplit = assetOwnersByType(ownerShipAsset, 'OWNER');
    let newOwnershipArr =
      ownershipSplitArray.length > 0 &&
      ownershipSplitArray.filter((owner) => owner.assetId === productId)
        .length > 0
        ? ownershipSplitArray
        : propsOwnershipSplit;
    createOwnership(newOwnerShip);
    getOwnerships(productId);

    newOwnershipArr.push(newOwnerShip);
    if (
      newOwnershipArr.length > 0 &&
      newOwnershipArr.reduce((memo, owner) => memo + owner.split, 0) === 100
    ) {
      SetReassignSplitErrorForOWNER(false);
    } else {
      SetReassignSplitErrorForOWNER(true);
    }
    setOwnershipSplitArray(newOwnershipArr);
  };
  const updateOwnershipOrderOnSubmit = (oldIndex: number, newIndex: number) => {
    const propsOwnershipSplit = assetOwnersByType(ownerShipAsset, 'OWNER');

    let newOwnership =
      ownershipSplitArray.length > 0 &&
      ownershipSplitArray.filter((owner) => owner.assetId === productId)
        .length > 0
        ? ownershipSplitArray
        : propsOwnershipSplit;
    let newOwners = arrayMove(newOwnership, oldIndex, newIndex);
    newOwners = newOwners.map((owner, index) => {
      owner.order = index;
      return owner;
    });
    if (
      newOwners.length > 0 &&
      newOwners.reduce((memo, owner) => memo + owner.split, 0) === 100
    ) {
      SetReassignSplitErrorForOWNER(false);
    } else {
      SetReassignSplitErrorForOWNER(true);
    }
    setOwnershipSplitArray(newOwners);
  };

  React.useEffect(() => {
    merchReleaseStepViewedEvent({
      step: 4,
      name: 'select project pricing',
      artist_id: artistId,
      artist_name: artistName,
      product_id: productId,
      product_type: printifyProductId,
      is_edit: isEdit,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (isUnlimited) {
      updateProductData({
        productName,
        productPrice,
        isUnlimited,
        quantity: undefined,
      });
    } else {
      updateProductData({
        productName,
        productPrice,
        isUnlimited: false,
        quantity: +quantity,
      });
    }
  }, [quantity, isUnlimited, productName, productPrice, updateProductData]);

  React.useEffect(() => {
    if (productId) {
      getOwnerships(productId);
    }
  }, [productId, getOwnerships]);

  React.useEffect(() => {
    scrollOnError();
  }, [validateErrors]);

  const handleNextStepClick = () => {
    setValidateForm(false);
    if (!invalidOwnershipTotal) {
      merchReleaseStepCompletedEvent({
        step: 4,
        name: 'select project pricing',
        artist_id: artistId,
        artist_name: artistName,
        product_id: productId,
        product_type: printifyProductId,
        is_edit: isEdit,
      });
      if (
        productSample &&
        productSample.receiveSample &&
        productId &&
        variants &&
        email
      ) {
        createSampleOrderRequest();
      } else {
        onNext();
      }
    } else {
      setValidateForm(true);
      setValidateErrors(validateErrors + 1);
    }
  };

  const TermsWrapper = () => {
    return (
      <div className="terms-box">
        <div className="terms">
          <Typography className="terms-text" weight="700" type="small">
            <Translate id="MERCH.PRODUCT.TERMS.TEXT.1" />{' '}
            <a href="/merch-agreement" className="terms-link" target="_blank">
              <Translate id="MERCH.PRODUCT.TERMS.TEXT.2" />
            </a>{' '}
            <Translate id="MERCH.PRODUCT.TERMS.TEXT.3" />
          </Typography>
        </div>
        <StyledButtonLoadingCtrl
          disabled={
            !(
              !!productData.productName.length &&
              Number(productData.productPrice) >= pricingData.production_cost &&
              isValidSampleOrder
            )
          }
          isLoading={receiveSample ? isLoading || isCreateOrderLoading : false}
          disableLoader={false}
          onClick={handleNextStepClick}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Typography
              className={`btn-text ${
                !(
                  !!productData.productName.length &&
                  Number(productData.productPrice) >=
                    pricingData.production_cost &&
                  isValidSampleOrder
                )
                  ? 'disabled'
                  : ''
              }`}
            >
              {receiveSample ? (
                <Translate id="MERCH.SECTIONS.PAY" />
              ) : (
                <Translate id="MERCH.SECTIONS.PUBLISH" />
              )}
            </Typography>
            <RightArrow
              className={`btn-arrow ${
                !(
                  !!productData.productName.length &&
                  Number(productData.productPrice) >=
                    pricingData.production_cost &&
                  isValidSampleOrder
                )
                  ? 'disabled'
                  : ''
              }`}
              isWhite
            />
          </Flex>
        </StyledButtonLoadingCtrl>
      </div>
    );
  };

  return (
    <MotionOpacity className={className}>
      {!isMobile && (
        <Typography className="mb-20" customFont="medium" type="big-title">
          <Translate id="MERCH.SECTIONS.PRICING.TITLE" />
        </Typography>
      )}
      <Grid>
        <Box noBorderBottom>
          <MerchProductDetail
            pricingData={pricingData}
            onChange={updateProductData}
          />
        </Box>
        <Box noBorderTop noBorderBottom>
          <MerchProductQuantity
            quantity={quantity}
            isUnlimited={isUnlimited}
            setQuantity={setQuantity}
            setIsUnlimited={setIsUnlimited}
          />
        </Box>
        <Box noBorderTop noBorderBottom>
          <OwnershipCtrl
            id="ownerDropdown"
            className="ownership"
            title={<Translate id="MERCH.SECTIONS.PRICING.OWNERS.TITLE" />}
            customInputLabel={
              <Translate id="MERCH.SECTIONS.PRICING.OWNERS.INPUT" />
            }
            songName={productData.productName}
            trackId={productId || ''}
            type="OWNER"
            assetType="MERCH"
            releaseId=""
            validate={validateForm}
            updateSplitRequest={() => null}
            reassignSplitError={reassignSplitErrorForOWNER}
            ownershipData={ownershipSplitArray}
            updateOrder={(oldIndex: number, newIndex: number) =>
              updateOwnershipOrderOnSubmit(oldIndex, newIndex)
            }
            // updateOwnershipData={() => null}
            updateSplit={updateOwnerSplit}
            removeSplit={onRemoveOwnerOwnership}
            createOwnershipOnSubmit={(newOwnerShip: Ownership) =>
              createOwnershipOnSubmit(newOwnerShip)
            }
            setGiveawayError={(_value) => null}
            withoutGiveMessage
            withAutosave
          />
        </Box>
        <MerchProductSample
          isLoading={isLoading}
          className={className}
          validationErrorId={validationErrorId}
        />
      </Grid>

      {!isLaptop ? (
        <FixedWrapper>
          <TermsWrapper />
        </FixedWrapper>
      ) : (
        <TermsWrapper />
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(MerchPricingOwners)`
  .terms-box {
    margin-top: 40px;

    .terms {
      padding: 20px;
      border-radius: 10px 10px 0 0;
      background-color: ${({ theme }) => theme.colors.box};

      .terms-text {
        line-height: 18px !important;
      }

      .terms-link {
        color: ${({ theme }) => theme.colors.error};
        opacity: 1;
        transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        &:hover {
          opacity: 0.5;
        }
      }
    }
  }
`);

const FixedWrapper = styled.div`
  position: fixed;
  top: 180px;
  right: 80px;
  width: ${getWidth(5)};
`;

const StyledButtonLoadingCtrl = styled(ButtonLoadingCtrl)`
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  min-width: auto;
  width: 100%;
  height: 77px;
  max-height: none;
  margin-top: 3px;

  .btn-text {
    color: ${({ theme }) => theme.colors.white};
    &.disabled {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }

  .btn-arrow {
    &.disabled {
      path {
        fill: ${({ theme }) => theme.colors.tertiary};
      }
    }
  }
`;
